import { AssetClass } from '@/entities/assets'

import { TRANSACTION_TYPE } from '@/entities/transactions/utils/const'

import { WizardAccountMode, WizardInvestor, WizardMark, WizardValues } from '../utils/types'
import { createHolding, ensureAccount, ensureTagIds } from '../utils/create'
import { useAssetPricesStore } from '@/store/assets/prices'

/**
 * One bundle per entity — an asset and a `subscribe and fund` transaction
 * each, held in that entity's own account, for its own share of the cost.
 *
 * private_security's own version of PrivateFundSubscribeAndCommit/create.ts:
 * same split mechanics (one cost divided by share, never each entity
 * independently holding the full amount — see PrivateFund/create.ts for
 * that sibling), the legacy wizard's own transaction verb, and a real
 * `assetType` read off the values instead of a fixed constant — this flow
 * has no type of its own, unlike a fund. No cash-flows step, unlike either
 * fund wizard: the legacy wizard this replaces never had one either, so
 * there is nothing here to post as a follow-up transaction.
 *
 * The name is the asset's, the same for every bundle: it is one holding,
 * however many entities split it. The ticker is what tells them apart —
 * `AssetClass.validate` requires one to be unique.
 *
 * Sequential, not `Promise.all`: two entities can both resolve to the same
 * new account name — a family that invests through the same trust twice in
 * one sentence — and firing both creates at once would race to make it twice.
 */
export const create = async (values: WizardValues): Promise<AssetClass[]> => {
  const tagIds = await ensureTagIds(values.tags)
  const investors: WizardInvestor[] = values.investors ?? []
  const totalShare = investors.reduce((sum, investor) => sum + (investor.share ?? 0), 0)

  const securities: AssetClass[] = []

  for (const investor of investors) {
    const accountId = await ensureAccount({
      id: investor.accountMode === WizardAccountMode.NEW ? undefined : investor.accountId,
      name: investor.accountName,
      type: investor.accountType,
      currency: values.currency,
    })

    const security = await createHolding({
      accountId,
      transactionType: TRANSACTION_TYPE.SUBSCRIBE_AND_FUND,
      name: values.assetName,
      ticker: `${values.assetName} (${investor.accountName})`,
      type: values.assetType,
      currency: values.currency,
      description: values.description,
      amount: investor.share ?? 0,
      date: values.date,
      tagIds,
    })

    /*
     * The values step's own marks, pro-rated to this bundle's own share of
     * the cost — the step asks for one current value for the whole holding,
     * not one per investor, and posting that same absolute number against
     * every bundle would multiply the position's value by however many
     * entities split it. Same reasoning as
     * PrivateFundSubscribeAndCommit/create.ts's own `createInvestorMarks`.
     */
    await createInvestorMarks(security.id, values.prices, totalShare ? (investor.share ?? 0) / totalShare : 0)

    securities.push(security)
  }

  return securities
}

/**
 * One investor's slice of the valuation step's own marks — see `create`'s
 * pro-rating note. `fraction` is this investor's share of the total, so the
 * sum of every investor's marks on a given date reconstructs the whole value
 * the step actually collected.
 */
const createInvestorMarks = async (
  assetId: string,
  marks: WizardMark[] = [],
  fraction: number,
): Promise<void> => {
  const pricesStore = useAssetPricesStore()

  for (const mark of marks) {
    await pricesStore.store({
      asset_id: assetId,
      date: mark.date,
      close: mark.close * fraction,
      source: mark.source,
    })
  }
}
